// Delivery Partner Dashboard — /delivery-partner
// Registration form → pending → active. Once active: assignments feed with 5s polling.
import React, { useCallback, useEffect, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { RefreshCw, Bike, MapPin, Phone, Package, CheckCircle2, XCircle, Truck, Home, Clock, IdCard, Upload, Loader2 } from 'lucide-react';
import DeliveryLeaderboard from '../../components/DeliveryLeaderboard';
import api from '../../utils/api';

const VEHICLES = ['cycle', 'bike', 'scooter', 'auto', 'car', 'van', 'walk'];

const STATUS_MAP = {
  broadcast: { label: 'Open', color: 'bg-amber-500 text-slate-900' },
  accepted: { label: 'Accepted', color: 'bg-cyan-500 text-slate-900' },
  picked_up: { label: 'Picked Up', color: 'bg-indigo-500 text-white' },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-purple-500 text-white' },
  delivered: { label: 'Delivered', color: 'bg-emerald-500 text-slate-900' },
  cancelled: { label: 'Cancelled', color: 'bg-rose-500 text-white' },
};

const STEP_ICONS = { accept: CheckCircle2, picked_up: Package, out_for_delivery: Truck, delivered: Home, decline: XCircle };

export default function DeliveryPartnerDashboard() {
  const [profile, setProfile] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const [assignments, setAssignments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyIds, setBusyIds] = useState({});
  const [filter, setFilter] = useState('active'); // active | open | history
  const [regBusy, setRegBusy] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', city: '', zone: '', vehicle_type: 'bike', vehicle_number: '', available_hours: '', id_proof_url: '', notes: '' });
  const [idUploading, setIdUploading] = useState(false);
  const [gpsOn, setGpsOn] = useState(() => localStorage.getItem('delivery_gps_broadcast') === '1');
  const [gpsStatus, setGpsStatus] = useState({ lat: null, lng: null, at: null, error: null });

  const loadProfile = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/delivery/me');
      setProfile(r.data.partner);
      setActiveCount(r.data.active_assignments || 0);
    } catch { setProfile(null); }
    finally { setBusy(false); }
  }, []);

  const loadAssignments = useCallback(async () => {
    if (!profile || profile.status !== 'active') return;
    try {
      const status = filter === 'active' ? 'mine' : filter;
      const r = await api.get(`/delivery/assignments?status=${status}`);
      setAssignments(r.data.assignments || []);
    } catch { /* silent */ }
  }, [profile, filter]);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);
  useEffect(() => {
    if (!profile || profile.status !== 'active') return;
    const t = setInterval(loadAssignments, 5000); // 5s real-time refresh
    return () => clearInterval(t);
  }, [profile, loadAssignments]);

  // === Live GPS broadcaster — sends `/gps` beacon every ~25s for the FIRST active assignment ===
  useEffect(() => {
    if (!gpsOn || !profile || profile.status !== 'active') return;
    if (!('geolocation' in navigator)) { setGpsStatus({ error: 'GPS not supported' }); return; }
    const active = assignments.find((a) => a.assigned_partner_id === profile.id
      && ['accepted', 'picked_up', 'out_for_delivery'].includes(a.status));
    if (!active) return;

    let watchId = null; let lastPost = 0;
    const post = async (pos) => {
      const now = Date.now();
      if (now - lastPost < 22000) return; // throttle to ~25s
      lastPost = now;
      try {
        await api.post(`/delivery/assignment/${active.id}/gps`, {
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy || null,
          speed_kmh: pos.coords.speed != null ? Math.max(0, pos.coords.speed * 3.6) : null,
          heading: pos.coords.heading != null && !isNaN(pos.coords.heading) ? pos.coords.heading : null,
        });
        setGpsStatus({ lat: pos.coords.latitude, lng: pos.coords.longitude, at: new Date().toISOString(), error: null });
      } catch (e) {
        setGpsStatus((s) => ({ ...s, error: e?.response?.data?.detail || 'beacon failed' }));
      }
    };
    watchId = navigator.geolocation.watchPosition(
      post,
      (err) => setGpsStatus((s) => ({ ...s, error: err.message || 'GPS denied' })),
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 },
    );
    return () => { if (watchId != null) navigator.geolocation.clearWatch(watchId); };
  }, [gpsOn, profile, assignments]);

  const submitRegistration = async () => {
    if (!form.name || !form.phone || !form.city) { toast.error('Name, phone, city required'); return; }
    setRegBusy(true);
    try {
      const r = await api.post('/delivery/register', form);
      toast.success('✅ Application submitted — admin approval ke baad activate hoga');
      setProfile(r.data.partner);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Registration failed');
    } finally { setRegBusy(false); }
  };

  const uploadIdProof = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setIdUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.url) {
        setForm((s) => ({ ...s, id_proof_url: r.data.url }));
        toast.success('📄 ID proof uploaded');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setIdUploading(false);
      e.target.value = '';
    }
  };

  const toggleAvailability = async () => {
    try {
      const r = await api.post('/delivery/me', { available: !profile.available });
      setProfile(r.data.partner);
      toast.success(r.data.partner.available ? '🟢 You are ONLINE' : '⚫ You are OFFLINE');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const act = async (id, action, note) => {
    setBusyIds((p) => ({ ...p, [id]: true }));
    try {
      await api.post(`/delivery/assignment/${id}/action`, { action, note: note || undefined });
      toast.success(`✅ ${action.replace('_', ' ')}`);
      loadAssignments(); loadProfile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setBusyIds((p) => ({ ...p, [id]: false })); }
  };

  // === Not registered → Registration form ===
  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950 text-white p-4 sm:p-6" data-testid="delivery-register-page">
        <Toaster position="top-right" richColors />
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 text-xs font-black mb-2">
              🛵 EARN AT · DELIVER PARCELS
            </div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Become a Delivery Partner
            </h1>
            <p className="text-xs opacity-80 mt-2">Har delivery pe seller ka set fee turant AT wallet me credit hoga. Admin approval ke baad start ho jaayega.</p>
          </div>
          <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-4 space-y-3" data-testid="delivery-register-form">
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1">Full Name *
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="delivery-reg-name"
                       className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm normal-case" />
              </label>
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1">Phone *
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="delivery-reg-phone"
                       placeholder="+91…" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm normal-case" />
              </label>
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1">City *
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="delivery-reg-city"
                       className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm normal-case" />
              </label>
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1">Zone / Area
                <input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="delivery-reg-zone"
                       placeholder="Localities served" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm normal-case" />
              </label>
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1">Vehicle
                <select value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} data-testid="delivery-reg-vehicle"
                        className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm normal-case">
                  {VEHICLES.map((v) => <option key={v} value={v} className="bg-slate-900">{v}</option>)}
                </select>
              </label>
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1">Vehicle Number
                <input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} data-testid="delivery-reg-vehicle-number"
                       placeholder="e.g. DL8CAB1234" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm normal-case" />
              </label>
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1 col-span-2 sm:col-span-1">
                <span className="flex items-center gap-1"><Clock size={11} /> Available Hours</span>
                <input value={form.available_hours} onChange={(e) => setForm({ ...form, available_hours: e.target.value })} data-testid="delivery-reg-hours"
                       placeholder="e.g. 10 AM – 8 PM" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm normal-case" />
              </label>
              <label className="text-[10px] font-black text-emerald-300 uppercase flex flex-col gap-1 col-span-2 sm:col-span-1">
                <span className="flex items-center gap-1"><IdCard size={11} /> ID Proof (Aadhaar/DL/PAN)</span>
                <div className="flex items-center gap-2">
                  <label data-testid="delivery-reg-id-pick"
                         className="flex items-center gap-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 hover:border-emerald-400 cursor-pointer text-xs normal-case flex-1">
                    {idUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    <span className="truncate">{form.id_proof_url ? 'Change photo' : 'Upload photo'}</span>
                    <input type="file" accept="image/*" onChange={uploadIdProof} className="hidden" disabled={idUploading} />
                  </label>
                  {form.id_proof_url && (
                    <img src={form.id_proof_url} alt="ID" data-testid="delivery-reg-id-preview"
                         className="w-10 h-10 rounded object-cover border border-emerald-400/60" />
                  )}
                </div>
              </label>
            </div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes (working hours, languages, etc.)"
                      data-testid="delivery-reg-notes" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            <button onClick={submitRegistration} disabled={regBusy} data-testid="delivery-reg-submit"
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 font-black text-sm disabled:opacity-40">
              {regBusy ? '⏳ Submitting…' : '📝 Submit Application'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === Pending / Rejected / Suspended → Status card ===
  if (profile.status !== 'active') {
    const map = {
      pending: { icon: '⏳', color: 'from-amber-500 to-orange-500', title: 'Application Under Review',
                 desc: 'Admin aapki application review kar raha hai. Approval ke baad turant activate ho jaayega.' },
      rejected: { icon: '❌', color: 'from-rose-500 to-red-600', title: 'Application Rejected',
                  desc: 'Aap dobara apply kar sakte ho — form re-submit karo.' },
      suspended: { icon: '⚠️', color: 'from-slate-500 to-gray-600', title: 'Account Suspended',
                   desc: 'Contact admin for reactivation.' },
    };
    const s = map[profile.status] || map.pending;
    return (
      <div className="min-h-screen bg-slate-950 text-white p-6 flex items-center justify-center" data-testid={`delivery-status-${profile.status}`}>
        <Toaster position="top-right" richColors />
        <div className={`max-w-md w-full rounded-2xl bg-gradient-to-br ${s.color} p-6 text-center shadow-2xl`}>
          <div className="text-5xl mb-3">{s.icon}</div>
          <div className="text-xl font-black">{s.title}</div>
          <div className="text-sm opacity-90 mt-2">{s.desc}</div>
          <div className="mt-4 text-[11px] opacity-80 bg-black/20 rounded-lg p-2 text-left">
            <div><b>Name:</b> {profile.name}</div>
            <div><b>City:</b> {profile.city}</div>
            <div><b>Vehicle:</b> {profile.vehicle_type} {profile.vehicle_number ? `· ${profile.vehicle_number}` : ''}</div>
            {profile.available_hours && <div><b>Hours:</b> {profile.available_hours}</div>}
          </div>
          {profile.status === 'rejected' && profile.reject_reason && (
            <div className="mt-3 p-2 rounded-lg bg-rose-900/60 border border-rose-400/60 text-left text-[11px]" data-testid="delivery-reject-reason">
              <div className="font-black text-rose-200 mb-0.5">📝 Rejection Reason:</div>
              <div className="text-rose-100">{profile.reject_reason}</div>
            </div>
          )}
          {profile.status === 'rejected' && (
            <button onClick={() => setProfile(null)} data-testid="delivery-reapply"
                    className="mt-4 px-4 py-2 rounded-lg bg-white text-slate-900 font-black text-xs">↻ Re-apply</button>
          )}
        </div>
      </div>
    );
  }

  // === Active partner dashboard ===
  const filtered = assignments;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-emerald-950 text-white pb-24" data-testid="delivery-dashboard">
      <Toaster position="top-right" richColors />
      <div className="max-w-3xl mx-auto p-3 sm:p-4">
        {/* Profile header */}
        <div className="rounded-2xl bg-gradient-to-r from-emerald-600 to-cyan-600 p-4 flex items-center gap-3 flex-wrap" data-testid="delivery-profile-card">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl">
            <Bike size={26} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-black">{profile.name}</div>
            <div className="text-[11px] opacity-90 flex items-center gap-2">
              <MapPin size={10} /> {profile.city}{profile.zone ? ` · ${profile.zone}` : ''} · <span className="uppercase">{profile.vehicle_type}</span>
            </div>
            <div className="text-[11px] opacity-90">
              📦 {profile.total_deliveries || 0} deliveries · 💰 <b className="text-yellow-300">{Number(profile.total_earned_at || 0).toFixed(0)} AT</b> earned
              {profile.rating_count > 0 && (
                <> · ⭐ <b className="text-yellow-300">{Number(profile.rating_avg || 0).toFixed(1)}</b> <span className="opacity-70">({profile.rating_count})</span></>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button onClick={toggleAvailability} data-testid="delivery-toggle-availability"
                    className={`px-4 py-2 rounded-full text-xs font-black ${
                      profile.available ? 'bg-emerald-400 text-slate-900 shadow-lg shadow-emerald-500/50' : 'bg-slate-800 text-slate-300'
                    }`}>
              {profile.available ? '🟢 ONLINE' : '⚫ OFFLINE'}
            </button>
            <button onClick={() => {
              const next = !gpsOn;
              setGpsOn(next); localStorage.setItem('delivery_gps_broadcast', next ? '1' : '0');
              toast.success(next ? '📡 GPS broadcasting ON' : '📡 GPS broadcasting OFF');
              if (!next) setGpsStatus({ lat: null, lng: null, at: null, error: null });
            }} data-testid="delivery-toggle-gps"
                    className={`px-3 py-1 rounded-full text-[10px] font-black ${
                      gpsOn ? 'bg-cyan-400 text-slate-900' : 'bg-slate-800 text-slate-400'
                    }`}>
              {gpsOn ? '📡 GPS ON' : '📡 GPS OFF'}
            </button>
          </div>
        </div>
        {gpsOn && (
          <div className="mt-2 text-[10px] opacity-90 flex items-center gap-2 px-3 py-1 rounded bg-cyan-500/10 border border-cyan-400/40" data-testid="delivery-gps-status">
            {gpsStatus.error ? (
              <><span className="text-rose-300">⚠️ {gpsStatus.error}</span></>
            ) : gpsStatus.at ? (
              <><span>📍 <b>{gpsStatus.lat?.toFixed(5)}, {gpsStatus.lng?.toFixed(5)}</b></span>
                <span className="opacity-70">· Last beacon {Math.max(0, Math.floor((Date.now() - new Date(gpsStatus.at).getTime()) / 1000))}s ago</span></>
            ) : (
              <span>📡 Waiting for GPS fix — allow browser permission</span>
            )}
          </div>
        )}

        {/* Filter chips */}
        <div className="flex gap-2 mt-4" data-testid="delivery-filter-bar">
          {[
            { key: 'active', label: `🔥 Active (${activeCount})` },
            { key: 'open', label: '📢 Open Broadcasts' },
            { key: 'history', label: '📜 History' },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`delivery-filter-${f.key}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-black ${filter === f.key ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
              {f.label}
            </button>
          ))}
          <button onClick={loadAssignments} data-testid="delivery-refresh" className="ml-auto p-2 rounded-lg bg-slate-800">
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Assignments feed */}
        <div className="mt-3 space-y-2">
          {filtered.length === 0 && (
            <div className="text-center py-12 opacity-60 text-sm" data-testid="delivery-empty">
              {!profile.available ? '⚫ Go ONLINE to see new deliveries' :
               filter === 'active' ? '✅ No active deliveries — abhi koi order nahi' :
               filter === 'open' ? '📢 No open broadcasts in your city — wait karo' :
               '📜 No history yet'}
            </div>
          )}
          {filtered.map((a) => {
            const st = STATUS_MAP[a.status] || { label: a.status, color: 'bg-slate-600 text-white' };
            const isBusy = !!busyIds[a.id];
            const isOpen = a.status === 'broadcast';
            const isMine = a.assigned_partner_id === profile.id;
            const done = ['delivered', 'cancelled'].includes(a.status);
            return (
              <div key={a.id} className={`rounded-2xl border p-3 space-y-2 ${
                isOpen ? 'bg-gradient-to-r from-amber-900/40 to-slate-900 border-amber-400/60 shadow-lg shadow-amber-500/20 animate-pulse-slow' :
                done ? 'bg-slate-900/50 border-slate-800' : 'bg-slate-900 border-slate-700'
              }`} data-testid={`delivery-card-${a.id.slice(0,8)}`}>
                <div className="flex items-start gap-2 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-black flex items-center gap-2">
                      🏪 {a.shop_name || '—'}
                    </div>
                    <div className="text-[10px] opacity-70">
                      <Phone size={9} className="inline" /> <a href={`tel:${a.buyer_phone}`} className="hover:text-emerald-300">{a.buyer_phone || '—'}</a> · <MapPin size={9} className="inline" /> {a.buyer_city || '—'}
                    </div>
                    <div className="text-[9px] opacity-50 font-mono">#{a.order_id.slice(0, 8)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-yellow-300">{Number(a.delivery_fee_at || 0).toFixed(0)}<span className="text-[10px]"> AT</span></div>
                    <div className="text-[9px] opacity-70">order {Number(a.order_total_at || 0).toFixed(0)} AT</div>
                    <div className={`text-[9px] px-2 py-0.5 rounded-full inline-block mt-0.5 font-black uppercase ${st.color}`}>{st.label}</div>
                  </div>
                </div>

                <div className="text-xs opacity-90 line-clamp-2">🛍️ {a.items_summary || '—'} · {a.item_count} items</div>
                {a.buyer_address && <div className="text-[10px] opacity-80">📍 {a.buyer_address}</div>}

                {/* Actions */}
                {isOpen && (
                  <div className="flex gap-2 pt-2 border-t border-slate-800">
                    <button onClick={() => act(a.id, 'accept')} disabled={isBusy} data-testid={`delivery-${a.id.slice(0,8)}-accept`}
                            className="flex-1 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black disabled:opacity-40">
                      ✅ Accept
                    </button>
                    <button onClick={() => act(a.id, 'decline')} disabled={isBusy} data-testid={`delivery-${a.id.slice(0,8)}-decline`}
                            className="px-3 py-2 rounded-lg bg-rose-600 text-white text-xs font-black disabled:opacity-40">
                      ✗ Skip
                    </button>
                  </div>
                )}
                {isMine && !done && (
                  <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-800">
                    {a.status === 'accepted' && (
                      <button onClick={() => act(a.id, 'picked_up')} disabled={isBusy} data-testid={`delivery-${a.id.slice(0,8)}-picked_up`}
                              className="flex-1 py-2 rounded-lg bg-indigo-500 text-white text-xs font-black disabled:opacity-40">📦 Picked Up</button>
                    )}
                    {['accepted', 'picked_up'].includes(a.status) && (
                      <button onClick={() => act(a.id, 'out_for_delivery')} disabled={isBusy} data-testid={`delivery-${a.id.slice(0,8)}-out_for_delivery`}
                              className="flex-1 py-2 rounded-lg bg-purple-500 text-white text-xs font-black disabled:opacity-40">🛵 Out for Delivery</button>
                    )}
                    <button onClick={() => act(a.id, 'delivered')} disabled={isBusy} data-testid={`delivery-${a.id.slice(0,8)}-delivered`}
                            className="flex-1 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black disabled:opacity-40">🏠 Delivered</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* City leaderboard — motivation + healthy competition */}
        <div className="mt-4">
          <DeliveryLeaderboard city={profile.city} limit={5} compact />
        </div>
      </div>
      <style>{`@keyframes pulse-slow { 0%,100% { box-shadow: 0 0 20px -4px rgba(245,158,11,0.4); } 50% { box-shadow: 0 0 30px -2px rgba(245,158,11,0.9); } } .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }`}</style>
    </div>
  );
}
